<script setup lang="ts">
/**
 * Updates, on a screen of their own.
 *
 * These used to be a view inside the support widget, reached by a button most
 * people never pressed — so a notice about their own account waited until
 * somebody went looking for it. Worse, the widget's launcher is hidden below
 * `md`, which meant on a phone there was no way to reach them at all.
 *
 * So they come to the person instead: if anything is unread when the page
 * loads, this opens by itself. Seeing them is what reading them means, which is
 * why each page is marked read as it is shown rather than asking anybody to
 * tick anything. The widget's Updates button now only says "show this" — it
 * draws nothing.
 *
 * Everything waiting is one sequence, one thing to a page, Previous and Next.
 * The version before this one paged the announcements and then stacked the work
 * on the account underneath as a list, so the second kind was whatever happened
 * to be below the fold — on a phone, out of sight entirely.
 */
import { splitIntoLinkSegments } from "~/utils/linkHelper";

const supportStore = useSupportStore();

/*
 * Named isOpen, not open: an unqualified `open` in a script setup block is the
 * global window.open, so the dialog silently bound to a function instead of to
 * this state.
 */
const isOpen = computed({
  get: () => supportStore.updatesOpen,
  set: (value: boolean) =>
    value ? supportStore.openUpdates() : supportStore.closeUpdates(),
});

const items = computed(() => supportStore.inbox);
const isLoading = computed(() => supportStore.isLoadingInbox);
const task = computed(() => supportStore.task);
const isLoadingTask = computed(() => supportStore.isLoadingTask);

type InboxItem = (typeof items.value)[number];

/**
 * Whether this is something we were sent, rather than work on the account.
 *
 * The two kinds are marked read differently, so the question is asked in one
 * place: an announcement or a message is marked by its ref, a task update by
 * the activity behind it — or, from an older server that sends no activity, by
 * the agent's one inbox watermark. readFor below is where that is decided.
 */
const isAnnouncement = (item: InboxItem) =>
  item.type === "announcement" || item.type === "message";

const announcements = computed(() => supportStore.announcements);

const taskUpdates = computed(() =>
  items.value.filter((item) => item.type === "task_update"),
);

/**
 * The sequence to walk: announcements first, then work on the account, each in
 * the server's order.
 *
 * Nothing here looks at `unread`, on purpose. A page flipping to read as it is
 * shown changes nothing about where it sits, so the cursor never finds a
 * different page under it halfway through somebody reading.
 *
 * Not sorted together by date either, and that is for an older server. One that
 * sends no `activity_id` has no per-item read for a task update, only a single
 * watermark per agent, and the only safe place to write that is the last page,
 * after everything in front of it has been walked. That works only while the
 * task updates are one unbroken block at the back. Against a current server
 * every page clears itself and the order is simply announcements before work.
 */
const pages = computed<InboxItem[]>(() => [
  ...announcements.value,
  ...taskUpdates.value,
]);

/** Where we are in that walk. */
const cursor = ref(0);

const current = computed(() => pages.value[cursor.value] ?? null);

const currentIsAnnouncement = computed(
  () => !!current.value && isAnnouncement(current.value),
);

const hasNext = computed(() => cursor.value < pages.value.length - 1);
const hasPrevious = computed(() => cursor.value > 0);

/*
 * How many pages are still unread, of both kinds.
 *
 * Counted from the pages rather than from the store's unread announcements: a
 * task update nobody has read is as new as an announcement, and a chip that
 * left it out vanished from a dialog that had just opened because of one.
 */
const unreadPages = computed(
  () => pages.value.filter((item) => item.unread).length,
);

/*
 * Whether this open has landed on a page yet.
 *
 * Landing is going to the first unread page and marking it read. It happens
 * once an open and must never happen twice. The read flips that page to read
 * at once, so a second landing finds the first unread page one further on and
 * marks that too: two pages read, one of them never on screen, and the next
 * refresh skips past it.
 *
 * Two places can land. One is the open below. The other is the watcher on the
 * page count, which fires when a reopen's refetch changes how many pages there
 * are — from nothing to something, most often. Nothing fixes which of the two
 * runs first, so whichever gets there first lands and the other finds this set
 * and does nothing.
 */
let landed = false;

/*
 * The inbox is fetched again when this is reopened, and when the auto-open
 * retry below lands, so the sequence can change underneath somebody sitting in
 * it.
 *
 * Shorter: without the clamp the cursor points past the end, `current` is null
 * and the dialog goes blank.
 *
 * Arriving on a dialog that is open and has not landed: somebody pressed the
 * widget's Updates button before the first fetch came back, so the open found
 * nothing to land on. Its first page is now on screen without having been
 * landed on or read, so both happen now. Opening again cannot bring this back,
 * because the dialog is already open and nothing changes for the watcher below
 * to see. A sequence that empties while open counts as not landed, so whatever
 * fills it next is landed on the same way.
 */
watch(
  () => pages.value.length,
  (length) => {
    if (cursor.value > length - 1) cursor.value = Math.max(0, length - 1);

    if (length === 0) landed = false;

    if (isOpen.value) land();
  },
);

/** Whether this has been opened once already in this page's life. */
let openedBefore = false;

const formatDateTime = (date: string) =>
  new Intl.DateTimeFormat("default", {
    dateStyle: "medium",
    timeStyle: "short",
  }).format(new Date(date));

/** What kind of thing this page is, said on the page rather than in the title. */
const kindLabel = (item: InboxItem) =>
  item.type === "task_update"
    ? "Work on your account"
    : item.type === "message"
    ? "Message"
    : "Announcement";

/**
 * Whether the task behind the current page is expanded underneath it.
 *
 * Expanded in place rather than pushed as a second screen with a Back button:
 * the detail is part of the page it belongs to, so coming out of it cannot lose
 * anybody's place in the sequence, and the paging controls never disappear
 * while somebody is halfway through reading.
 */
const detailsOpen = ref(false);

const closeDetails = () => {
  detailsOpen.value = false;
  supportStore.clearTask();
};

const openDetails = async () => {
  const item = current.value;

  if (!item) return;

  detailsOpen.value = true;

  await supportStore.fetchTask(item.ref);

  /*
   * The store keeps no request generation, so a slow first fetch resolves after
   * a fast second one and leaves the previous page's task sitting under this
   * page's header. If the page moved on while this was in flight, it is not
   * ours to show.
   */
  if (current.value?.id !== item.id) closeDetails();
};

/** The scrolling part of the dialog — the body scrolls, the footer does not. */
const scroller = ref<HTMLElement | null>(null);

/*
 * Page two starts at the top of page two. The dialog's DOM survives being
 * closed and reopened, so without this a new page inherits the scroll offset of
 * whatever was read before it and opens halfway down its own text.
 */
const scrollToTop = () => {
  nextTick(() => scroller.value?.scrollTo({ top: 0 }));
};

const reset = () => {
  cancelPendingRead();
  closeDetails();
};

/*
 * A read waiting on the tab coming back to the front.
 *
 * Held here so it can be torn down: a dialog that was closed an hour ago should
 * not mark something read because somebody finally clicked back into the tab.
 * Closing, paging and unmounting all go through cancelPendingRead, and so does
 * every new read, so there is never more than one of these.
 */
let awaitingVisibility: (() => void) | null = null;

const cancelPendingRead = () => {
  if (awaitingVisibility) {
    document.removeEventListener("visibilitychange", awaitingVisibility);
    awaitingVisibility = null;
  }
};

/**
 * What showing this page should send, or null for nothing.
 *
 * Asked when the page arrives, and asked again if the read had to wait for the
 * tab, rather than trusted from the first time: the inbox may have changed in
 * between, and a page that is no longer last has things behind it.
 *
 * An announcement or a message is marked by its ref, a task update by the
 * activity behind it, and each only while it is still unread.
 *
 * A task update WITHOUT an activity id came from an older server, which has no
 * per-item read for one — only the agent's single watermark — and that is
 * written from the last page and nowhere else. Anywhere earlier it would clear
 * updates nobody has looked at. On the last page it clears nothing unseen: the
 * dialog lands on the first unread page and Next moves one at a time, so
 * everything in front of the last page has already been shown. Only the
 * watermark, and NOT markInboxRead(): that one posts an empty body, which the
 * server reads as "every announcement this agent can see", including any the
 * 30-item inbox window never returned. Walking to the end of what was shown is
 * not evidence about what was not.
 */
const readFor = (page: InboxItem): (() => void) | null => {
  if (isAnnouncement(page)) {
    return page.unread
      ? () => supportStore.markAnnouncementRead(page.ref)
      : null;
  }

  if (page.type !== "task_update") return null;

  if (typeof page.activity_id === "number") {
    const activityId = page.activity_id;

    return page.unread
      ? () => supportStore.markTaskUpdateRead(activityId)
      : null;
  }

  const isLastPage = cursor.value === pages.value.length - 1;

  return isLastPage && taskUpdates.value.some((item) => item.unread)
    ? () => supportStore.markTaskUpdatesRead()
    : null;
};

/**
 * Mark whatever is on screen read — and only that, and as soon as it is seen.
 *
 * This is the whole behaviour: the page that was shown counts and the pages
 * that were not stay unread, so closing after the first means a refresh opens
 * on the second. Marking everything the moment the dialog appeared would make
 * "next time, show me the next one" impossible.
 *
 * There is no delay before it counts. There used to be one, and closing the
 * dialog inside it — which is what somebody does with a dialog that opened by
 * itself — cancelled the read, so the same page came back on every refresh.
 *
 * What it does wait for is the tab being visible. This opens by itself, so a
 * laptop opened on this page at nine o'clock must not mark the morning's
 * notices read while it sat closed. When the tab comes to the front the read
 * goes through only if the dialog is still open and the same page is still the
 * one showing.
 *
 * Safe to call twice for one page. The store drops a read that is already on
 * its way, and a page it has marked is no longer unread, so readFor finds
 * nothing to send.
 */
const readCurrent = () => {
  cancelPendingRead();

  const showing = current.value;

  if (!showing) return;

  const send = readFor(showing);

  if (!send) return;

  if (document.visibilityState === "visible") {
    send();
    return;
  }

  awaitingVisibility = () => {
    if (document.visibilityState !== "visible") return;

    cancelPendingRead();

    /*
     * Still open, and still the same one? Compared by id rather than by ref:
     * refs are only unique within a kind, so announcement 41 and task 41 share
     * a ref while the sequence holds both.
     */
    const page = current.value;

    if (!isOpen.value || !page || page.id !== showing.id) return;

    readFor(page)?.();
  };

  document.addEventListener("visibilitychange", awaitingVisibility);
};

onBeforeUnmount(() => {
  cancelPendingRead();

  if (retryTimer) {
    clearTimeout(retryTimer);
    retryTimer = null;
  }
});

/**
 * Start on the first thing they have not seen, of either kind.
 *
 * Not simply index 0: somebody who read the top one yesterday and has two older
 * unread ones should be taken to the first of those rather than shown something
 * they have already dealt with.
 *
 * A task update is a landing page too. From a current server it is marked read
 * the moment it is shown, like an announcement, which is what moves the next
 * refresh on to the one after it. From an older server it can only be cleared
 * by the watermark on the last page, so there the dialog keeps opening until
 * somebody has walked to the end. Announcements sort ahead of task updates, so
 * landing on a task update already implies every announcement has been read.
 */
const startAtFirstUnread = () => {
  const first = pages.value.findIndex((item) => item.unread);

  cursor.value = first === -1 ? 0 : first;
};

/**
 * Land this open: the first unread page, from the top, marked read.
 *
 * Once an open and no more, which is what `landed` is for. With no pages yet
 * it does nothing and stays owed, and the watcher on the page count lands when
 * they arrive.
 */
const land = () => {
  if (landed || pages.value.length === 0) return;

  landed = true;

  startAtFirstUnread();
  scrollToTop();
  readCurrent();
};

/**
 * Move to a page.
 *
 * One door for both directions so every arrival is the same: the detail opened
 * on the page being left is dropped, this page is marked read, and the reader
 * is put at the top of what they just asked for.
 */
const goTo = (index: number) => {
  if (index < 0 || index > pages.value.length - 1) return;

  cursor.value = index;

  closeDetails();
  scrollToTop();
  readCurrent();
};

const next = () => goTo(cursor.value + 1);

const previous = () => goTo(cursor.value - 1);

/**
 * Out to the full history.
 *
 * This dialog is the queue of what is waiting: one thing at a time, and only
 * what has not been seen. Everything ever sent lives on a page of its own, so
 * this is the quiet way across for somebody who wants to go back and find
 * something they have already read and cannot page to from here.
 *
 * Closed first, then navigated. The dialog is appended to the body rather than
 * to the page that opened it, so a route change does not take it down on its
 * own — leaving it open would drop the reader on the new page with the old
 * dialog still sitting over it.
 */
const seeAllUpdates = async () => {
  isOpen.value = false;

  await navigateTo("/whats-new");
};

watch(isOpen, async (nowOpen) => {
  // Every open lands afresh. A close owes nothing, so it clears this too.
  landed = false;

  if (!nowOpen) {
    reset();
    return;
  }

  /*
   * The first open already has fresh data — the load below fetched it, and
   * that is what decided to open at all. Every later one is somebody pressing
   * the button, who should not be shown whatever was true when they arrived.
   */
  if (openedBefore) {
    await supportStore.fetchInbox(true);

    /*
     * The dialog may have been closed during that request. Without this the
     * walk below marks whatever it landed on read, with nothing on screen to
     * have read.
     */
    if (!isOpen.value) return;
  }

  openedBefore = true;

  /*
   * Not an unconditional landing. The refetch above can change how many pages
   * there are while this is waiting, and the watcher on the page count may have
   * landed on them already. Landing again here would skip the page
   * it has just marked read and mark the next one read as well.
   */
  land();
});

onMounted(async () => {
  // Asked for here rather than borrowed from the widget: this has to work on a
  // phone, where the widget's launcher is never drawn.
  await attemptAutoOpen();
});

/**
 * Open for anything unread in this dialog's own sequence, from EITHER signal.
 *
 * Two of them, unioned on purpose, because each one misses something the other
 * catches:
 *
 * `announcementsUnread` is counted on the server across every announcement
 * visible to this agent. The inbox items are a capped window, so an unread
 * announcement older than that window is counted there and appears nowhere in
 * `pages` — reading the items alone meant the dialog stopped opening for
 * exactly the notices that had been waiting longest.
 *
 * The items, in turn, are the only place an unread TASK update shows up:
 * `announcementsUnread` counts announcements and nothing else, by design.
 *
 * Deliberately NOT the store's mixed `unread`, which also counts ticket
 * replies. Those are the chat widget's business and appear nowhere in this
 * sequence, so opening for one would show a dialog with nothing unread in it
 * that came back on every refresh.
 */
const openIfAnythingUnread = (result?: { announcementsUnread?: number }) =>
  (result?.announcementsUnread ?? 0) > 0 ||
  pages.value.some((item) => item.unread);

let retryTimer: ReturnType<typeof setTimeout> | null = null;

/**
 * Ask, and if we never got to ask, ask once more.
 *
 * The support token is minted lazily on the first request, and the layout also
 * bootstraps one on a five-second timer. A first fetch that loses that race —
 * or meets a dropped connection — used to leave the dialog silently disabled
 * for the whole session, because a failed fetch and an empty inbox looked
 * identical from here. One retry, and only when the fetch genuinely failed.
 */
const attemptAutoOpen = async () => {
  const result = await supportStore.fetchInbox(true);

  if (openIfAnythingUnread(result)) {
    supportStore.openUpdates();
    return;
  }

  if (result?.ok === false) {
    retryTimer = setTimeout(async () => {
      retryTimer = null;
      if (openIfAnythingUnread(await supportStore.fetchInbox(true))) {
        supportStore.openUpdates();
      }
    }, 6000);
  }
};

const dotClass = (level: string) =>
  ({
    critical: "bg-red-500",
    warning: "bg-amber-500",
    success: "bg-emerald-500",
    info: "bg-light-purple dark:bg-light-purple-bar",
  })[level] ?? "bg-light-purple dark:bg-light-purple-bar";
</script>

<template>
  <el-dialog
    v-model="isOpen"
    align-center
    append-to-body
    class="support-updates-dialog"
    :class="{ 'has-no-pages': !pages.length }"
  >
    <template #header>
      <!--
        The title on its own line, the chips under it.

        Side by side they fight for the same inches on a 360px screen — the
        close button already owns the top right corner — and the title is the
        half that loses, breaking mid-phrase. Named for what the dialog holds
        rather than for one of the two kinds in it, since either can be on
        screen; which kind this page is, the page itself says.
      -->
      <div class="min-w-0">
        <h2
          class="text-base font-semibold text-gray-900 dark:text-white sm:text-lg"
        >
          Updates
        </h2>

        <!--
          How many pages are still waiting, of both kinds, and the way out to
          the whole history. Where you are is in the footer, beside the buttons
          that change it — twice on one small screen is noise.

          The row itself is no longer conditional, only the chip on it. The link
          belongs here even when nothing is unread, because "Nothing new." is
          exactly the moment somebody goes looking for what they have already
          read. In the header rather than the footer so it never crowds Previous
          and Next, and so it survives the empty state, whose footer is hidden.

          The link is small type on a target that is not: 11px words inside a
          32px box. This dialog is the phone's only way to these, and a bare
          line of 11px text is about fifteen pixels tall — too little to hit
          reliably, sitting as it does under a chip and beside the close button.
          The same 32px the What's New page gives its own inline actions.
        -->
        <div class="mt-1.5 flex flex-wrap items-center gap-x-3 gap-y-1.5">
          <span
            v-if="unreadPages > 0"
            class="rounded-full bg-red-500 px-2 text-[10px] font-bold leading-5 text-white"
          >
            {{ unreadPages }} new
          </span>

          <button
            type="button"
            class="inline-flex min-h-[32px] items-center rounded text-[11px] font-semibold text-light-purple underline-offset-2 transition hover:underline focus:outline-none focus-visible:ring-2 focus-visible:ring-light-sec-purple dark:text-light-purple-border"
            @click="seeAllUpdates"
          >
            See all updates
          </button>
        </div>
      </div>
    </template>

    <!--
      One thing at a time, with a way to the next.

      A list marked everything read the moment it appeared, so anything below
      the fold was "read" without being seen. One at a time means only what was
      actually on screen counts, and what is left waits for the next visit.
    -->
    <div ref="scroller" class="updates-scroller">
      <div
        v-if="isLoading && !pages.length"
        class="grid place-content-center py-10 text-sm text-gray-500 dark:text-gray-300"
      >
        Loading...
      </div>

      <div
        v-else-if="!pages.length"
        class="grid place-content-center py-10 text-center text-sm text-gray-500 dark:text-gray-300"
      >
        Nothing new.
      </div>

      <!--
        The page. Both kinds wear the same card — dot, what it is, title, who
        and when, then the words — so paging from one to the other is not a
        change of scenery. Keyed so a video or an embed on one announcement is
        torn down rather than handed to the next.
      -->
      <article v-else-if="current" :key="current.id">
        <div class="flex items-start gap-2.5">
          <span
            class="mt-2 h-2 w-2 shrink-0 rounded-full"
            :class="dotClass(current.level)"
          />

          <div class="min-w-0 flex-1">
            <div class="flex flex-wrap items-center gap-1.5">
              <span
                class="rounded-full bg-light-feature px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide text-light-purple dark:bg-white/10 dark:text-light-purple-border"
              >
                {{ kindLabel(current) }}
              </span>

              <span
                v-if="current.status"
                class="rounded-full bg-gray-100 px-2 py-0.5 text-[10px] font-semibold text-gray-600 dark:bg-white/10 dark:text-gray-300"
              >
                {{ current.status }}
              </span>
            </div>

            <h3
              class="mt-1 text-base font-semibold text-gray-900 dark:text-white"
            >
              {{ current.title }}
            </h3>

            <p class="mt-0.5 text-[11px] text-gray-500 dark:text-gray-400">
              {{ current.from }}
              <template v-if="current.at">
                · {{ formatDateTime(current.at) }}
              </template>
            </p>
          </div>
        </div>

        <!--
          The picture or the clip, under the title and above the words.

          It used to come after the body, which on a long announcement buried it
          under a wall of text: the thing the announcement was written around was
          the one part nobody scrolled far enough to see. It carries the most in
          the least time, so it goes where the eye already is.

          No key of its own, and it needs none: the key on <article> above rebuilds
          this whole page when current.id changes, which is what tears a playing
          video down rather than handing it to the next announcement.
        -->
        <AgentsAnnouncementMedia
          v-if="current.media && current.media.length"
          :media="current.media"
        />

        <!--
          Not clamped. This is the whole thing, on a page of its own.

          An address in the words is something to press. Support staff paste
          meeting links and URLs into these, and they used to arrive as letters
          nobody could click, leaving the reader to select one by hand — on the
          phone this dialog is the only way to these at all.

          The body is split into text and links rather than turned into markup,
          and every piece below is rendered by Vue as a text node. An
          announcement is somebody else's words, so no HTML is ever built out of
          one: splitIntoLinkSegments is what makes that safe, and it is the same
          helper the support chat already renders its messages with, so an
          address is the same thing to press wherever it is read.
        -->
        <p
          v-if="current.body"
          class="mt-3 whitespace-pre-wrap break-words text-sm text-gray-700 dark:text-gray-200"
        >
          <template
            v-for="(segment, segmentIndex) in splitIntoLinkSegments(
              current.body,
            )"
            :key="segmentIndex"
          >
            <!--
              The link colour of the surface it is on, which is this card and
              NOT the chat's purple bubble — the bubble's links are white,
              because they sit on purple, and white on this card is nothing at
              all. These are the two the rest of the dialog already spends on
              every word worth pressing: light-purple on the white card, and
              light-purple-border on the dark one. The underline carries it for
              anybody who cannot tell the two colours apart, which colour alone
              never does.

              The focus ring changes with the theme for the same reason the text
              does: light-sec-purple on the dark card is about 2.9:1 against it,
              under the 3:1 a focus ring has to clear to be seen at all.

              No wrapping class of its own, deliberately. The scroller sets
              `overflow-wrap: anywhere` and this paragraph `break-words`, and
              both of those inherit, so a pasted URL longer than the dialog
              breaks inside the card rather than widening it.
            -->
            <a
              v-if="segment.href"
              :href="segment.href"
              target="_blank"
              rel="noopener noreferrer"
              class="text-light-purple underline underline-offset-2 focus:outline-none focus-visible:ring-2 focus-visible:ring-light-sec-purple dark:text-light-purple-border dark:focus-visible:ring-light-purple-border"
              >{{ segment.text }}</a
            >
            <template v-else>{{ segment.text }}</template>
          </template>
        </p>

        <!--
          The work behind an update: what it is, where it got to.

          Fetched only when asked for — a page that is paged past should not
          have cost a request.
        -->
        <div v-if="!currentIsAnnouncement" class="mt-4">
          <button
            class="inline-flex min-h-[40px] w-full items-center justify-center rounded-lg border border-light-purple/40 px-4 text-sm font-semibold text-light-purple transition hover:bg-light-feature dark:border-light-purple-bar dark:text-light-purple-border dark:hover:bg-white/10 sm:w-auto"
            @click="detailsOpen ? closeDetails() : openDetails()"
          >
            {{ detailsOpen ? "Hide details" : "View details" }}
          </button>

          <div
            v-if="detailsOpen"
            class="mt-3 rounded-lg border border-gray-200 p-3 dark:border-dark-primary-border"
          >
            <div
              v-if="isLoadingTask"
              class="grid place-content-center py-6 text-sm text-gray-500 dark:text-gray-300"
            >
              Loading...
            </div>

            <div
              v-else-if="!task"
              class="grid place-content-center py-6 text-center text-sm text-gray-500 dark:text-gray-300"
            >
              Not available.
            </div>

            <template v-else>
              <div class="flex flex-wrap items-center gap-2">
                <span
                  v-if="task.code"
                  class="text-[11px] font-bold text-gray-400"
                  >{{ task.code }}</span
                >
                <span
                  class="rounded-full px-2 py-0.5 text-[10px] font-bold"
                  :class="
                    task.resolved
                      ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-500/20 dark:text-emerald-300'
                      : 'bg-blue-100 text-blue-700 dark:bg-blue-500/20 dark:text-blue-200'
                  "
                  >{{ task.status }}</span
                >
                <span
                  v-for="type in task.types"
                  :key="type"
                  class="rounded-full bg-gray-100 px-2 py-0.5 text-[10px] font-semibold text-gray-600 dark:bg-white/10 dark:text-gray-300"
                  >{{ type }}</span
                >
              </div>

              <p class="mt-1 font-semibold text-gray-900 dark:text-white">
                {{ task.title }}
              </p>

              <p
                v-if="task.description"
                class="mt-1 whitespace-pre-wrap text-sm text-gray-600 dark:text-gray-300"
              >
                {{ task.description }}
              </p>

              <p class="mt-1 text-[11px] text-gray-400">
                <template v-if="task.due_date"
                  >Due {{ task.due_date }} ·
                </template>
                <template v-if="task.completed_at">
                  Completed {{ formatDateTime(task.completed_at) }}
                </template>
                <template v-else-if="task.created_at">
                  Raised {{ formatDateTime(task.created_at) }}
                </template>
              </p>

              <!-- What it breaks down into -->
              <div v-if="task.subtasks.length" class="mt-3">
                <p
                  class="text-[11px] font-bold uppercase tracking-wide text-gray-400"
                >
                  Steps
                </p>
                <ul class="mt-1 space-y-1">
                  <li
                    v-for="(step, index) in task.subtasks"
                    :key="index"
                    class="flex items-start gap-2 text-sm text-gray-700 dark:text-gray-200"
                  >
                    <span
                      class="mt-0.5 grid h-4 w-4 shrink-0 place-items-center rounded-full border"
                      :class="
                        step.done
                          ? 'border-emerald-500 bg-emerald-500 text-white'
                          : 'border-gray-300 dark:border-gray-600'
                      "
                    >
                      <svg
                        v-if="step.done"
                        class="h-2.5 w-2.5"
                        viewBox="0 0 24 24"
                        fill="currentColor"
                      >
                        <path
                          d="M9 16.2 4.8 12l-1.4 1.4L9 19 21 7l-1.4-1.4L9 16.2Z"
                        />
                      </svg>
                    </span>
                    <span
                      class="min-w-0"
                      :class="step.done && 'text-gray-400 line-through'"
                      >{{ step.title }}</span
                    >
                  </li>
                </ul>
              </div>

              <!-- Files. A new tab is fine here: this is a dialog, not a conversation -->
              <div v-if="task.attachments.length" class="mt-3">
                <p
                  class="text-[11px] font-bold uppercase tracking-wide text-gray-400"
                >
                  Files
                </p>
                <div class="mt-1 grid gap-1">
                  <a
                    v-for="file in task.attachments"
                    :key="file.url"
                    :href="file.url"
                    target="_blank"
                    rel="noopener"
                    class="flex min-h-[44px] items-center gap-2 rounded-lg border border-gray-200 p-2 text-left hover:bg-gray-50 dark:border-dark-primary-border dark:hover:bg-dark-feature"
                  >
                    <span class="min-w-0 flex-1">
                      <span
                        class="block truncate text-xs font-semibold text-gray-800 dark:text-gray-200"
                        >{{ file.name }}</span
                      >
                      <span class="block text-[10px] text-gray-400">{{
                        file.size
                      }}</span>
                    </span>
                  </a>
                </div>
              </div>

              <!-- Only what was written for them -->
              <div v-if="task.comments.length" class="mt-3">
                <p
                  class="text-[11px] font-bold uppercase tracking-wide text-gray-400"
                >
                  Updates
                </p>
                <div class="mt-1 space-y-2">
                  <div
                    v-for="(comment, index) in task.comments"
                    :key="index"
                    class="rounded-lg bg-gray-50 p-2 dark:bg-dark-feature"
                  >
                    <p
                      class="whitespace-pre-wrap text-sm text-gray-700 dark:text-gray-200"
                    >
                      {{ comment.body }}
                    </p>
                    <p class="mt-0.5 text-[10px] text-gray-400">
                      {{ comment.by }}
                      <template v-if="comment.at">
                        · {{ formatDateTime(comment.at) }}</template
                      >
                    </p>
                  </div>
                </div>
              </div>
            </template>
          </div>
        </div>
      </article>
    </div>

    <!--
      Moving through them, in the footer so they stay put.

      Next is the emphasised one while anything is ahead: the point of the
      dialog is to get through what is new, and a person who stops halfway meets
      the rest next time rather than losing them. Both ends are disabled rather
      than hidden, so the pair does not jump about as you reach them, and both
      are 44px tall because on a phone this is the only thing to press.
    -->
    <template #footer>
      <div v-if="pages.length" class="flex items-center justify-between gap-2">
        <button
          class="min-h-[44px] min-w-[88px] rounded-lg px-3 text-sm font-medium transition disabled:opacity-40"
          :class="
            hasPrevious
              ? 'text-gray-600 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-white/10'
              : 'text-gray-400'
          "
          :disabled="!hasPrevious"
          @click="previous"
        >
          Previous
        </button>

        <!-- Which one of how many, for anybody who wants to know how far in they are -->
        <span class="shrink-0 text-[11px] text-gray-500 dark:text-gray-400">
          {{ cursor + 1 }} of {{ pages.length }}
        </span>

        <button
          class="min-h-[44px] min-w-[88px] rounded-lg px-3 text-sm font-semibold transition disabled:opacity-40"
          :class="
            hasNext
              ? 'bg-light-purple text-white hover:opacity-90 dark:bg-light-purple-bar'
              : 'text-gray-400'
          "
          :disabled="!hasNext"
          @click="next"
        >
          Next
        </button>
      </div>
    </template>
  </el-dialog>
</template>

<style>
/*
 * Deliberately NOT scoped. el-dialog teleports itself to the end of <body>, so
 * a scoped rule compiles to `[data-v-xxx] .support-updates-dialog` and matches
 * nothing — the dialog has no ancestor carrying the component's attribute. The
 * first version of this rule was scoped and therefore never applied at all: on
 * a narrow screen the dialog kept its 540px and ran off the side.
 *
 * Every rule is keyed off .support-updates-dialog, which is only ever on this
 * dialog, so unscoped is safe. Pairing it with .el-dialog is not decoration
 * either: main.css sets `.el-dialog { min-width: 350px }` and, in dark mode,
 * `.dark .el-dialog { margin: 15vh auto 50px }` — a single class does not
 * outrank those, two do.
 */

/*
 * A column: header and footer keep their height, the body is the only thing
 * that scrolls. That is what pins Previous and Next — they live in the footer,
 * outside the scroller, so no amount of announcement can push them away.
 *
 * The cap belongs here rather than on the body, which is the mistake the
 * earlier max-h-[70vh] made: 70vh of body plus a header plus a footer plus a
 * top margin is more than the screen on a short landscape phone, and the
 * buttons ended up under the fold. Capping the dialog itself makes the sum
 * arithmetically impossible. dvh keeps a phone's retracting address bar out of
 * the measurement; the vh line before it is the fallback for browsers that do
 * not know dvh, and must stay first.
 */
.support-updates-dialog.el-dialog {
  display: flex;
  flex-direction: column;
  /*
   * The width is set here, as the variable Element Plus reads, rather than by
   * the dialog's `width` prop — which is why that prop is gone from the
   * template.
   *
   * The prop does not write a width. It writes `--el-dialog-width` into the
   * element's own inline style, and an inline custom property outranks every
   * stylesheet, so the prop and the media queries below could never both be
   * obeyed: the prop would win at every size and the desktop widths would do
   * nothing. Defining the variable keeps one width in one place, and both
   * `width: var(--el-dialog-width, 50%)` declarations that exist — Element
   * Plus's own and the dark-mode copy of it in main.css — resolve to it
   * without either of them having to be outranked or marked important.
   */
  --el-dialog-width: 540px;
  max-width: 92vw;
  /* Element Plus floors every dialog at 350px, which is wider than the content
     box of a 360px phone once the dialog's own padding is counted. */
  min-width: 0;
  max-height: 86vh;
  max-height: 86dvh;
  overflow: hidden;
  /* align-center asks for a centred dialog and gets it by way of `margin: auto`
     — which the dark-mode rule in main.css overwrites with a 15vh top margin,
     pushing the dialog down until its footer is off the bottom of a phone. */
  margin: auto !important;
}

.support-updates-dialog .el-dialog__header,
.support-updates-dialog .el-dialog__footer {
  flex: 0 0 auto;
}

/*
 * The global header padding is 30px 22px !important — a tenth of a phone screen
 * spent on nothing — and its right edge runs under the close button, which
 * main.css pins 16px from the right at 27px wide.
 */
.support-updates-dialog .el-dialog__header {
  padding: 16px 56px 12px 20px !important;
}

.support-updates-dialog .el-dialog__body {
  flex: 1 1 auto;
  min-height: 0;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  padding: 0;
}

/*
 * The scroller is ours rather than the body itself so that paging can put the
 * reader back at the top of the new page; the padding rides with it so the
 * scrollbar sits at the edge of the dialog and not inside the text.
 */
.support-updates-dialog .updates-scroller {
  flex: 1 1 auto;
  min-height: 0;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
  padding: 16px 20px;
  /* An announcement is written by a person, who will one day paste a URL with
     no space in it that is wider than the phone. */
  overflow-wrap: anywhere;
}

.support-updates-dialog .el-dialog__footer {
  padding: 10px 16px 14px;
  text-align: left;
  border-top: 1px solid rgb(0 0 0 / 0.08);
}

.dark .support-updates-dialog .el-dialog__footer {
  border-top-color: var(--dark-primary-border);
}

/* Nothing to page through: the footer would otherwise be a bare strip under an
   empty state. Element Plus draws the footer whenever the slot exists at all. */
.support-updates-dialog.has-no-pages .el-dialog__footer {
  display: none;
}

/* Whatever an announcement carries, it lives inside the page it is on. */
.support-updates-dialog .updates-scroller img,
.support-updates-dialog .updates-scroller video,
.support-updates-dialog .updates-scroller iframe {
  max-width: 100%;
}

/*
 * And capped in height, now that it comes first.
 *
 * The width rule above is not enough on its own. A picture is drawn inside an
 * aspect-ratio box, so its height is the dialog's width divided by the
 * picture's own ratio — the wider the dialog, the taller that box. At 900px an
 * ordinary landscape screenshot comes to roughly 480px and a portrait one off a
 * phone to over 1500px, so the media that was moved above the body to be seen
 * first would push every word of the body off the bottom instead. Widening the
 * dialog made that worse rather than better, which is the opposite of what the
 * width was for.
 *
 * 45vh keeps the opening of the body on the same screen as the picture at every
 * size. Nothing is cropped by it: the cap is on the `.el-image` wrapper, which
 * is the element the ratio is written on, AND on the `img` within it, which
 * otherwise keeps its full height and is clipped by the wrapper's own
 * `overflow: hidden`. Capping both, with neither width nor height fixed, is
 * what makes the picture scale down whole and keep its shape.
 *
 * A YouTube clip is deliberately not in this list. Its height comes from a
 * ratio box in AnnouncementMedia.vue rather than from the iframe, so a cap here
 * would leave that box standing at its old height with a black strip below the
 * player. It belongs in that file, with the other sizing it owns.
 */
.support-updates-dialog .updates-scroller .el-image,
.support-updates-dialog .updates-scroller .el-image__inner,
.support-updates-dialog .updates-scroller video {
  max-height: 45vh;
  max-height: 45dvh;
}

/* Once it is capped, a wide picture is narrower than the dialog it sits in.
   Centred so what it leaves over is even on both sides rather than all of it
   in one strip down the right. */
.support-updates-dialog .updates-scroller .el-image {
  text-align: center;
}

/*
 * Wider where there is room for it.
 *
 * At 540px a long announcement is a narrow column of text in the middle of an
 * empty desktop, and its picture is shrunk to fit a width the screen never
 * asked for. Wider means fewer lines, which means less of the announcement
 * below the fold — the width is what makes it one screen rather than a scroll.
 *
 * Stepped rather than a share of the viewport, because a percentage that reads
 * well on a 1440px screen is nearly edge to edge on a 1024px tablet. The top
 * step stops at 900px, which is where the app's other deliberately wide surface
 * stops too — the bulk import report in main.css.
 *
 * These only ever set the preferred width. `max-width: 92vw` above still
 * governs, so nothing here can make the dialog wider than the screen it is on,
 * and a window dragged narrower than the step it is in is clamped rather than
 * overflowed.
 */
@media (min-width: 1024px) {
  .support-updates-dialog.el-dialog {
    --el-dialog-width: 820px;
    /*
     * Taller as well, for the reason the width is wider: more of the
     * announcement at once. The 86vh cap was written against a short landscape
     * phone, which is not what is being measured here, and 90vh still leaves
     * 5vh of overlay above and below — the dialog is centred by `margin: auto`,
     * so the footer cannot be pushed off the bottom by this.
     */
    max-height: 90vh;
    max-height: 90dvh;
  }
}

@media (min-width: 1280px) {
  .support-updates-dialog.el-dialog {
    --el-dialog-width: 900px;
  }
}

@media (max-width: 640px) {
  .support-updates-dialog.el-dialog {
    max-height: 92vh;
    max-height: 92dvh;
  }

  .support-updates-dialog .el-dialog__header {
    padding: 14px 52px 10px 16px !important;
  }

  .support-updates-dialog .updates-scroller {
    padding: 14px 16px;
  }

  .support-updates-dialog .el-dialog__footer {
    padding: 8px 12px 12px;
  }
}
</style>
